import * as React from 'react';

import { useAnalytics } from '../hooks/useAnalytics';

export interface AnalyticsVideoProps extends React.VideoHTMLAttributes<HTMLVideoElement> {
  /**
   * Event name to track when the user plays the video.
   * @default "Promo Video Watched"
   */
  eventName?: string;
  /**
   * Video name/title to send.
   */
  videoName?: string;
  /**
   * Extra event properties to include.
   */
  eventProperties?: Record<string, unknown>;
}

function inferSectionName(el: HTMLElement): string | undefined {
  if (typeof el?.closest !== 'function') return undefined;
  return el.closest('[data-analytics-section-name]')?.getAttribute('data-analytics-section-name') ?? undefined;
}

export const AnalyticsVideo = React.forwardRef<HTMLVideoElement, AnalyticsVideoProps>(
  (
    {
      eventName = 'Promo Video Watched',
      videoName,
      eventProperties,
      onPointerDown,
      onTouchStart,
      onMouseDown,
      onKeyDown,
      onPlay,
      ...props
    },
    ref
  ) => {
    const { analytics, isInitialized } = useAnalytics();
    const userInitiatedRef = React.useRef(false);

    const markUserInitiated = () => {
      userInitiatedRef.current = true;
    };

    const handlePlay = (e: React.SyntheticEvent<HTMLVideoElement>) => {
      const video = e.currentTarget;

      // Preserve consumer handler first.
      onPlay?.(e);

      // Native media controls (play button) often won't trigger pointer/touch events on the <video>.
      // `isTrusted` helps distinguish user action vs programmatic/autoplay.
      const nativeIsTrusted =
        typeof (e.nativeEvent as unknown as { isTrusted?: boolean })?.isTrusted === 'boolean'
          ? Boolean((e.nativeEvent as unknown as { isTrusted?: boolean }).isTrusted)
          : false;

      const wasUserInitiated = userInitiatedRef.current || nativeIsTrusted;
      userInitiatedRef.current = false;

      if (!wasUserInitiated) return;
      if (!analytics || !isInitialized) return;

      const duration = Number.isFinite(video.duration) ? Math.round(video.duration) : 0;
      const isFullscreen = typeof document !== 'undefined' && document.fullscreenElement === video;
      const unmute = !video.muted && video.volume > 0;

      const el = video as unknown as HTMLElement;
      const inferredSectionName = inferSectionName(el);

      try {
        analytics.trackEvent(eventName, {
          ...(eventProperties ?? {}),
          ...(eventProperties && 'Section Name' in eventProperties
            ? {}
            : inferredSectionName
              ? { 'Section Name': inferredSectionName }
              : {}),
          ...(videoName ? { 'Video Name': videoName } : {}),
          'Video Duration': duration,
          'Full Screen': isFullscreen,
          Unmute: unmute,
        });
      } catch (error) {
        console.error(`Error tracking event "${eventName}":`, error);
      }
    };

    return (
      <video
        ref={ref}
        {...props}
        onPointerDown={(e) => {
          markUserInitiated();
          onPointerDown?.(e);
        }}
        onTouchStart={(e) => {
          markUserInitiated();
          onTouchStart?.(e);
        }}
        onMouseDown={(e) => {
          markUserInitiated();
          onMouseDown?.(e);
        }}
        onKeyDown={(e) => {
          // space/enter commonly triggers play when focused
          if (e.key === ' ' || e.key === 'Enter') {
            markUserInitiated();
          }
          onKeyDown?.(e);
        }}
        onPlay={handlePlay}
      />
    );
  }
);
AnalyticsVideo.displayName = 'AnalyticsVideo';

